<template>
  <div v-if="showFinanceInfo">
    <div class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required">{{ financeInfoLabel }} <span>*</span></label>
      <div v-if="questionLabels[0]">
        <label  class="tla-form-group__label"
          v-html="questionLabels[0]"></label>
      </div>
      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in options"
          :key="item.touchpointValue"
          :collectionName="'financeInfo'"
          :inputId="item.touchpointValue"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="true"
          :validationScope="validationScope"
          @financeInfo="submitFinanceInfo($event)">
          <div class="generic-touchpoint__text" slot="touchpoint" v-html="item.touchpointName"></div></touchpoint>
      </div>
      <span class="tla-form-group__message" :class="getValidationClass('financeInfo', 'message')">
        Please select an option.
      </span>
    </div>

    <div v-if="!isNotSurePurchaseType">
      <numberInputTouchPoint
        :step="12"
        :max="60"
        :min="0"
        :slider="true"
        :name="'financeTerm'"
        :precision="0"
        :prefix="''"
        :suffix="' Months'"
        :inputId="'NotSure'"
        :inputValue="'Not Sure'"
        :collectionName="'financeTerm'"
        :validationRules="''"
        v-model="financeTerm"
        :validationScope="validationScope"
        :label="'Contract Term <span>*</span>'"
        :interval="12"
        @financeTerm="onFinanceTermChangeEvent"/>

      <numberInputTouchPoint
        v-if="isPersonalContractPurchase"
        :step="1000"
        :max="30000"
        :min="0"
        :slider="true"
        :name="'annualMileage'"
        :precision="0"
        :prefix="''"
        :suffix="' Miles'"
        :inputId="'NotSure'"
        :inputValue="'Not Sure'"
        :collectionName="'annualMileage'"
        :validationRules="''"
        v-model="annualMileage"
        :validationScope="validationScope"
        :label="'Annual Mileage <span>*</span>'"
        :interval="1000"
        @annualMileage="onAnnualMileageChangeEvent"/>

      <div class="tla-form-group tla-form-group--margin-large">
        <label for="depositAmount" class="tla-form-group__label tla-form-group__label--required">Deposit Amount
          <span>*</span></label>
          <div v-if="questionLabels[1]">
            <label  class="tla-form-group__label"
              v-html="questionLabels[1]"></label>
          </div>
          <NumberInput
          id="depositAmount"
          name="depositAmount"
          v-model="depositAmount"
          :min="0"
          :max="20000"
          :step="1000"
          :precision="0"
          :slider="true"
          :fieldName="'depositAmount'"
          data-vv-name="depositAmount"
          :data-vv-scope="validationScope"
          :moneyValidationClass="getValidationClass('depositAmount', 'input', depositAmountChanged)"
          v-validate.initial="`min_value:1000`"
          :interval="1000"
          @depositAmount="depositAmount = $event">
        </NumberInput>
        <span class="tla-form-group__message"
          :class="getValidationClass('depositAmount', 'message', depositAmountChanged)">{{
            errors.first(`${validationScope}.depositAmount`) }}</span>
        <vue-slider v-if="!hideSliders" v-model="depositAmount" :min="0" :max="20000" height="10" :dot-size="26"
          :tooltip="false" :interval="1000" :class="{ 'vue-slider-component--no-value': depositAmount === 0 }" />
      </div>

      <div v-if="showBudgetSlider" class="tla-form-group tla-form-group--margin-large">
        <label for="monthlyBudget" class="tla-form-group__label tla-form-group__label--required">Monthly Budget (£)
          <span>*</span></label>
          <div v-if="questionLabels[2]">
            <label  class="tla-form-group__label"
              v-html="questionLabels[2]"></label>
          </div>
          <NumberInput
            id="monthlyBudget"
            name="monthlyBudget"
            v-model="monthlyBudget"
            :min="sliderFloor"
            :max="sliderFloor + budgetWindow"
            :step="50"
            :precision="0"
            :slider="true"
            :fieldName="'monthlyBudget'"
            data-vv-name="monthlyBudget"
            :data-vv-scope="validationScope"
            :moneyValidationClass="getValidationClass('monthlyBudget', 'input', monthlyBudgetChanged)"
            v-validate.initial="`min_value:${sliderFloor === 0 ? 50 : sliderFloor}`"
            @monthlyBudget="monthlyBudget = $event">
        </NumberInput>
        <span class="tla-form-group__message"
          :class="getValidationClass('monthlyBudget', 'message', monthlyBudgetChanged)">{{
            errors.first(`${validationScope}.monthlyBudget`) }}</span>
        <vue-slider v-if="!hideSliders" v-model="sliderValue" :min="sliderFloor" :max="sliderFloor + budgetWindow" height="10"
          :dot-size="26" :tooltip="false" :interval="50"
          :class="{ 'vue-slider-component--no-value': monthlyBudget < sliderFloor }" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch, Inject
} from 'vue-property-decorator';
import vueSlider from 'vue-slider-component';
import VeeValidate, { Validator } from 'vee-validate';
import {
  namespace
} from 'vuex-class';
import { validationService } from '@/services/ValidationService';
import mobileCheckService from '@/services/MobileCheckService';
import Touchpoint from '../../models/Touchpoint';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';
import PurchaseTypeGroup from '../../enums/PurchaseTypeGroup';
import PurchaseType from '../../enums/PurchaseType';
import IFinance from '../../models/interfaces/IFinance';
import NumberInput from '../widgets/common/NumberInput.vue';
import NumberInputTouchPointComponent from '../ui/NumberInputTouchPointComponent.vue';
import FinanceValidationMessages from '../../validation/finance';
import ExtraData from '../../models/ExtraData';
import {
  getLowerLimit, PCPInputs, BaseFinanceInputs, FinanceType
} from '../../utils/FinanceCalculators';

const VehicleModule = namespace('vehicleModule');

Vue.use(VeeValidate, FinanceValidationMessages);

@Component({
  components: {
    touchpoint: GenericTouchPointComponent,
    numberInputTouchPoint: NumberInputTouchPointComponent,
    NumberInput,
    vueSlider
  }
})
export default class FinanceInformationComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'What finance product are you considering?' })
  public financeInfoLabel!: string;

  @Prop({ default: false })
  public showIfFinance!: boolean;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: () => [] })
  public questionLabels!: string[];

  public options: Touchpoint[] = [
    new Touchpoint(`${PurchaseType.PersonalContractPurchase}`, 'Personal Contract Purchase (PCP)'),
    new Touchpoint(`${PurchaseType.HirePurchase}`, 'Hire Purchase (HP)'),
    new Touchpoint('NotSure', 'Not Sure')
  ];

  private isPersonalContractPurchase: boolean = false;

  private isNotSurePurchaseType: boolean = false;

  private depositAmountChanged: boolean = false;

  private monthlyBudgetChanged: boolean = false;

  private hideSliders: boolean = false;

  @VehicleModule.Getter public averagePrices!: any;

  get sliderValue(): number {
    // clamp into [floor, floor+window]
    const min = this.sliderFloor;
    const max = min + this.budgetWindow;
    return Math.min(Math.max(this.monthlyBudget, min), max);
  }

  set sliderValue(v: number) {
    const min = this.sliderFloor;
    const max = min + this.budgetWindow;
    // if v falls outside, it gets clamped
    if (v < min) {
      this.monthlyBudget = min;
    } else if (v > max) {
      this.monthlyBudget = max;
    } else {
      this.monthlyBudget = v;
    }
  }

  public async submitFinanceInfo(value: string) {
    if (value !== 'NotSure') {
      const purchaseType = parseInt(value, 10);
      this.$store.dispatch('financeModule/submitPurchaseTypes', [purchaseType]);
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'financeInfoNotSure');

      this.isNotSurePurchaseType = false;
      this.isPersonalContractPurchase = purchaseType === PurchaseType.PersonalContractPurchase;

      if (!this.isPersonalContractPurchase) {
        this.annualMileage = 0;
      }

      this.purchaseTypes = [purchaseType];
    } else {
      this.isPersonalContractPurchase = false;
      this.annualMileage = 0;

      await this.$store.dispatch('financeModule/submitResetPurchaseTypeGroupFields');

      this.isNotSurePurchaseType = true;
      const extraData: ExtraData = new ExtraData('financeInfoNotSure', 'true');
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);

      this.$store.dispatch('financeModule/submitPurchaseTypes', [PurchaseType.PersonalContractPurchase, PurchaseType.HirePurchase]);
      this.purchaseTypes = [PurchaseType.PersonalContractPurchase, PurchaseType.HirePurchase];
    }

    this.$validator.errors.removeById('fake-finance-info');
    this.$nextTick(() => {
      this.$validator.validateAll(this.validationScope);
    });
  }

  public onFinanceTermChangeEvent(newVal: number) {
    this.financeTerm = newVal;
  }

  public onAnnualMileageChangeEvent(newVal: number) {
    this.annualMileage = newVal;
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged: boolean = false) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  public get finance(): IFinance {
    return this.$store.getters['financeModule/finance'];
  }

  public get purchaseTypes(): PurchaseType[] {
    return this.$store.getters['financeModule/purchaseTypes'];
  }

  public set purchaseTypes(purchaseTypes: PurchaseType[]) {
    this.$store.dispatch('financeModule/submitPurchaseTypes', purchaseTypes);
  }

  public get purchaseTypeGroup(): PurchaseTypeGroup {
    return this.$store.getters['financeModule/purchaseTypeGroup'];
  }

  public get showFinanceInfo(): boolean {
    return ((this.purchaseTypeGroup === PurchaseTypeGroup.Finance && this.showIfFinance) || !this.showIfFinance);
  }

  @Watch('showFinanceInfo', { immediate: true })
  public onshowFinanceInfoChanged(newValue: boolean) {
    if (newValue) {
      this.$validator.errors.add({
        field: 'fake-finance-info',
        msg: 'Please answer.',
        scope: this.validationScope,
        id: 'fake-finance-info'
      });
    } else {
      this.$validator.errors.removeById('fake-finance-info');
    }
    this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'financeInfoNotSure');
  }

  public get financeTerm(): number {
    return this.finance.financeTerm;
  }

  public set financeTerm(value: number) {
    const clamped = Math.min(Math.max(value, 0), 60);
    this.$store.dispatch('financeModule/submitFinanceTerm', clamped);
  }

  public get annualMileage(): number {
    return this.finance.annualMileage;
  }

  public set annualMileage(value: number) {
    const clamped = Math.min(Math.max(value, 0), 30000);
    this.$store.dispatch('financeModule/submitAnnualMileage', clamped);
  }

  public get monthlyBudget(): number {
    return this.finance.monthlyBudget;
  }

  public get depositAmount(): number {
    return this.finance.depositAmount;
  }

  public set depositAmount(value: number) {
    const clamped = Math.min(Math.max(value, 0), 20000);
    this.$store.dispatch('financeModule/submitDepositAmount', clamped);
  }

  public set monthlyBudget(value: number) {
    this.$store.dispatch('financeModule/submitMonthlyBudget', value);
  }

  private budgetWindow = 1000;

  @Watch('depositAmount')
  public ondepositAmountChange(newVal: number, oldVal: number) {
    this.depositAmountChanged = oldVal !== newVal;
  }

  @Watch('monthlyBudget')
  public onMonthlyBudgetChange(newVal: number, oldVal: number) {
    this.monthlyBudgetChanged = oldVal !== newVal;
  }

  // Computed for conditional display of slider
  public get showBudgetSlider(): boolean {
    return true
      || (!this.isNotSurePurchaseType
        && this.purchaseTypeGroup === PurchaseTypeGroup.Finance)
      || this.currentVehicle.manufacturerId !== 34;
  }

  // Compute the dynamic lower limit for the budget slider
  public get sliderFloor(): number {
    return 0;
    /*
    if (this.currentVehicle.manufacturerId !== 34) return 0;

    // We're assuming here that a value of 0 means not set. This is a reasonable assumption
    // as zeroes in these fields are not valid. It would make this code more complicated
    // to start querying the store for not sure extra data.
    const deposit = this.depositAmount > 0 ? this.depositAmount : this.averagePrices[0] * 0.1;
    const termMonths = this.financeTerm > 0 ? this.financeTerm : 48;
    const annualMileage = this.annualMileage > 0 ? this.annualMileage : 10000;

    const inputs: BaseFinanceInputs & Partial<PCPInputs> = {
      rrp: this.averagePrices[0],
      deposit,
      termMonths,
      annualMileage
    };
    try {
      const type = this.purchaseTypes[0] === PurchaseType.PersonalContractPurchase ? 'pcp' : 'hp';
      const floor = getLowerLimit(type as FinanceType, inputs as any);
      // clamp out infinities / NaN
      return Number.isFinite(floor) ? floor : 0;
    } catch {
      return 0;
    }
    */
  }

  public get currentVehicle() {
    return this.$store.getters['vehicleModule/currentVehicle'];
  }

  public created() {
    const v: any = this.$validator;

    // swallow only the “Validating a non-existent field” errors
    const wrap = (orig: Function) => (...args: any[]) => orig(...args).catch((err: any) => {
      if (err && /Validating a non-existent field/.test(err.message)) {
        return Promise.resolve(false);
      }
      return Promise.reject(err);
    });

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    v.validate = wrap(v.validate.bind(v));
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    v.validateAll = wrap(v.validateAll.bind(v));
    this.$validator.localize('en', FinanceValidationMessages.english);
    this.hideSliders = mobileCheckService.hideSliders;
  }
}
</script>
